import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import Container from '../shared/Container/Container';
import Button from '../shared/Button/Button';
import { trackCTAClick, trackCarouselInteraction, trackNavigation } from '../../utils/analytics';
import './Hero.css';

const Hero = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = [
    {
      src: '/images/ceramic_home_edition.jpg',
      alt: 'Rib being dipped into Rib-n-Dip custom elongated BBQ sauce cup for perfect sauce coverage',
      label: 'The Perfect Dunk',
    },
    {
      src: '/images/restaurant_tray_takeout.jpg',
      alt: 'BBQ restaurant takeout tray with Rib-n-Dip wholesale rib dipping sauce cups',
      label: 'Restaurant Ready',
    },
    {
      src: '/images/bark_n_dip.png',
      alt: 'Bark-n-Dip — cute pet-edition cup and rib-shaped bone treat for dogs',
      label: 'Bark-n-Dip — Fur the Pups',
    },
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      trackNavigation('scroll', id);
    }
  };

  // Auto-advance carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [images.length]);

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
    trackCarouselInteraction('hero_carousel', 'indicator', index);
  };

  const goToPrevious = () => {
    const newIndex = (currentImageIndex - 1 + images.length) % images.length;
    setCurrentImageIndex(newIndex);
    trackCarouselInteraction('hero_carousel', 'previous', newIndex);
  };

  const goToNext = () => {
    const newIndex = (currentImageIndex + 1) % images.length;
    setCurrentImageIndex(newIndex);
    trackCarouselInteraction('hero_carousel', 'next', newIndex);
  };

  return (
    <section className="hero">
      <Container>
        <div className="hero__content">
          <motion.div
            className="hero__text"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h1 className="hero__title">
              Custom Rib Dipping Sauce Cups - Dip Ribs the Right Way
            </h1>
            <p className="hero__subtitle">
              The first elongated BBQ sauce cup designed for full-rib flavor — every inch sauced, every bite perfect. Perfect for restaurants, food trucks, and home BBQ enthusiasts.
            </p>
            <p className="hero__description">
              Whether you run a rib shack or rule the backyard grill, Rib-n-Dip gives you the dunk you've always wanted.
            </p>
            <div className="hero__cta-group">
              <Button
                variant="primary"
                onClick={() => {
                  trackCTAClick('Shop Restaurant Packs', 'shop', 'Restaurant Packs');
                  scrollToSection('restaurant');
                }}
              >
                Shop Restaurant Packs
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  trackCTAClick('Explore Home Edition', 'explore', 'Ceramic Edition');
                  scrollToSection('home-bbq');
                }}
              >
                Explore Home Edition
              </Button>
            </div>
          </motion.div>

          <motion.div
            className="hero__image"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="hero__carousel">
              <AnimatePresence initial={false}>
                <motion.div
                  key={currentImageIndex}
                  className="hero__carousel-slide"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.6, ease: 'easeInOut' }}
                >
                  <img
                    src={images[currentImageIndex]?.src || ''}
                    alt={images[currentImageIndex]?.alt || ''}
                    className="hero__image-main"
                  />
                  <div className="hero__image-label">
                    {images[currentImageIndex]?.label}
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Carousel Controls */}
              <button
                className="hero__carousel-button hero__carousel-button--prev"
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                className="hero__carousel-button hero__carousel-button--next"
                onClick={goToNext}
                aria-label="Next image"
              >
                ›
              </button>

              {/* Carousel Indicators */}
              <div className="hero__carousel-indicators">
                {images.map((_, index) => (
                  <button
                    key={index}
                    className={`hero__carousel-indicator ${
                      index === currentImageIndex ? 'active' : ''
                    }`}
                    onClick={() => goToImage(index)}
                    aria-label={`Go to image ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </Container>
      <div className="hero__shimmer"></div>
    </section>
  );
};

export default Hero;
